import { Flex, TableRowProps, useBoolean } from '@chakra-ui/react'
import React from 'react'
import { useNavigate } from 'react-router'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { CallOutcomeCell } from '~/pages/Calls/cells/CallOutcomeCell'
import { ContactCell } from '~/pages/Calls/cells/ContactCell'
import { ContactCombinedCell } from '~/pages/Calls/cells/ContactCombinedCell'
import { DateTimeCell } from '~/pages/Calls/cells/DateTimeCell'
import { DurationCell } from '~/pages/Calls/cells/DurationCell'
import { TranscriptCell } from '~/pages/Calls/cells/TranscriptCell'
import { useCallsListItemStyles } from '~/pages/Calls/componentStyles/CallsListItem.style'
import {
  CallTarget,
  getCallOutcomeData,
  getCallTargetData,
} from '~/pages/Calls/data/CallDetailData'
import { Size } from '~/types/ThemeTypes'
import { hasValue } from '~/utils/common'
import { formatSeconds } from '~/utils/formatter'

import { formatDateString, formatTimeString } from '@mangovoice/core'
import { Call, CallStatus } from '@mangovoice/types'

import {
  createCallTableStyleContext,
  useCallTableStyles,
} from './componentStyles/CallsTable.style'
import { CallTableColumn } from './data/CallTableData'

const [TableStylesProvider] = createCallTableStyleContext()

interface CallLogsListItemProps extends TableRowProps {
  call: Call
  isSelected?: boolean
  tableSize: Size
  onSelected?: (callId: number) => void
  isFirst?: boolean
  onTranscriptClick?: () => void
}

export const CallListItem = ({
  call,
  isSelected,
  tableSize,
  isFirst,
  onSelected,
  onTranscriptClick,
}: CallLogsListItemProps) => {
  const [isHovered, setIsHovered] = useBoolean(false)

  const customerSentimentScore = call.call_sentiment?.customer_overall_score

  const tableStyles = useCallTableStyles({ size: tableSize })
  const styles = useCallsListItemStyles({ size: tableSize })
  const createdDate = new Date(call.created_at)
  const date = formatDateString(createdDate)
  const time = formatTimeString(createdDate)
  const outcome = getCallOutcomeData(call)
  const hasVoicemail = hasValue(call.voicemail_id)
  const fromData = getCallTargetData(call, CallTarget.From)
  const toData = getCallTargetData(call, CallTarget.To)
  const durationString = formatSeconds(call.duration_in_seconds)
  const navigate = useNavigate()

  const handleVoicemailClick = () => {
    navigate(`${ApplicationRoutes.VOICEMAIL}/${call.voicemail_id}`)
  }

  const handleTranscriptClicked = () => {
    onTranscriptClick?.()
  }

  const handleHoverStart = () => {
    setIsHovered.on()
  }
  const handleHoverEnd = () => {
    setIsHovered.off()
  }

  return (
    <Flex
      sx={styles.container}
      aria-selected={isSelected}
      onClick={() => onSelected?.(call.id)}
      data-first={isFirst}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
    >
      <Flex sx={styles.rowWrapper}>
        <TableStylesProvider value={tableStyles}>
          <DateTimeCell
            columnStyles={tableStyles[CallTableColumn.DateTime]}
            date={date}
            time={time}
          />
          <CallOutcomeCell
            columnStyles={tableStyles[CallTableColumn.Outcome]}
            text={outcome.text}
            icon={outcome.icon}
            direction={call.direction}
            variant={outcome.variant}
            size={tableSize}
            onVoicemailClick={handleVoicemailClick}
          />
          {tableSize == Size.md || tableSize == Size.lg ? (
            <>
              <ContactCell
                columnStyles={tableStyles[CallTableColumn.Contact]}
                title={fromData.titleValue ?? ''}
                subtitle={fromData.subtitleValue}
                contact={fromData.contact}
                number={fromData.number}
                isExtension={fromData.isExtension}
                isHovered={isHovered}
              />
              <ContactCell
                columnStyles={tableStyles[CallTableColumn.Contact]}
                title={toData.titleValue ?? ''}
                subtitle={toData.subtitleValue}
                contact={toData.contact}
                number={toData.number}
                isExtension={toData.isExtension}
                isHovered={isHovered}
              />
            </>
          ) : (
            <ContactCombinedCell
              columnStyles={tableStyles[CallTableColumn.Contact]}
              fromTitle={fromData.titleValue ?? ''}
              toTitle={toData.titleValue ?? ''}
              fromContact={fromData.contact}
              toContact={toData.contact}
              fromNumber={fromData.number}
              toNumber={toData.number}
              fromSubtitle={fromData.subtitleValue}
              toSubtitle={toData.subtitleValue}
              isFromExtension={fromData.isExtension}
              isToExtension={toData.isExtension}
            />
          )}
          <DurationCell
            columnStyles={tableStyles[CallTableColumn.Duration]}
            duration={durationString}
            hasVoicemail={hasVoicemail}
            pbxId={call.pbx.id}
            callId={call.id}
            transcriptId={call.transcript_id}
            customerSentimentScore={customerSentimentScore}
            onTranscriptClick={handleTranscriptClicked}
            onVoicemailClick={handleVoicemailClick}
            tableSize={tableSize}
            voicemailId={call.voicemail_id}
            isCallInProgress={call.status === CallStatus.InProgress}
          />
          {(tableSize == Size.lg || tableSize == Size.md) && (
            <TranscriptCell
              columnStyles={tableStyles[CallTableColumn.Transcript]}
              hasVoicemail={hasVoicemail}
              callId={call.id}
              pbxId={call.pbx.id}
              transcriptId={call.transcript_id}
              customerSentimentScore={customerSentimentScore}
              onTranscriptClick={handleTranscriptClicked}
              onVoicemailClick={handleVoicemailClick}
              voicemailId={call.voicemail_id}
              transcriptionStatus={call.transcription_status}
            />
          )}
        </TableStylesProvider>
      </Flex>
    </Flex>
  )
}
